import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase/config';
import { collection, query, where, getDocs, getDoc, addDoc, serverTimestamp, doc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuthorization } from '../../contexts/AuthorizationContext';
import { FaBars, FaTimes, FaClipboardList, FaBook, FaSearch, FaClipboardCheck, FaPlay, FaEdit, FaTrash } from 'react-icons/fa';
import './MyLists.css';
import '../navbar/Navbar.css';

function MyLists() {
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const { hasPermission } = useAuthorization();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleMenuClick = () => {
    setIsOpen(!isOpen);
  };

  const handleLinkClick = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const fetchTemplates = async () => {
    setError(null);
    
    try {
      const userId = currentUser?.uid || auth.currentUser?.uid;
      const templatesRef = collection(db, 'templates');
      const q = query(
        templatesRef,
        where('userId', '==', userId),
        where('isTemplate', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const templateData = await Promise.all(querySnapshot.docs.map(async doc => {
        const data = doc.data();
        let locations = [];
        
        if (data.locations && data.locations.length > 0) {
          const locationsSnapshot = await getDocs(query(
            collection(db, 'locations'),
            where('id', 'in', data.locations)
          ));
          
          locations = locationsSnapshot.docs.map(doc => doc.data().name);
        }
        
        return {
          id: doc.id,
          ...data,
          locations
        };
      }));
      
      setTemplates(templateData);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to load templates. Please try again.');
    }
  };

  useEffect(() => {
    const userId = currentUser?.uid || auth.currentUser?.uid;
    
    if (userId) {
      fetchTemplates();
    } else if (currentUser === null && !auth.currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const getTemplateData = async (templateId) => {
    const templateRef = doc(db, 'tasklists', templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (!templateSnap.exists()) {
      return null;
    }

    return {
      id: templateSnap.id,
      ...templateSnap.data()
    };
  };

  const handleTemplateClick = async (templateId) => {
    try {
      const templateRef = doc(db, 'templates', templateId);
      const templateSnap = await getDoc(templateRef);
      
      if (!templateSnap.exists()) {
        throw new Error('Template not found');
      }

      const templateData = templateSnap.data();
      console.log('Template data:', templateData); // Debug log
      
      // Create a deep copy of template items to preserve all properties
      const items = templateData.items?.map(item => ({
        ...JSON.parse(JSON.stringify(item)), // Deep copy all properties
        id: crypto.randomUUID(),
        isCompleted: false,
        timestamp: new Date().toISOString(),
        backgroundColor: item.backgroundColor,
        fontSize: item.fontSize
      })) || [];

      const formInstance = {
        name: templateData.name,
        items,
        originalTemplateId: templateId,
        templateVersion: templateData.templateVersion || 1,
        status: 'in-progress',
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp(),
        userId: currentUser.uid,
        locationId: templateData.assignedLocations?.[0]?.id || null,
        isTemplate: false,
        // Copy styling properties
        fontSizes: templateData.fontSizes,
        templateStyles: templateData.templateStyles,
        assignedLocations: templateData.assignedLocations || [],
        type: templateData.type || 'default'
      };

      console.log('Form instance to be created:', formInstance); // Debug log

      const formsRef = collection(db, 'forms');
      const docRef = await addDoc(formsRef, formInstance);
      
      if (!docRef.id) {
        throw new Error('Failed to create form instance');
      }

      navigate('/forms/active');
    } catch (error) {
      console.error('Error creating form:', error);
      setError('Failed to create form from template. Please try again.');
    }
  };

  const handleTemplateDelete = async (templateId) => {
    setTemplateToDelete(templateId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteDoc(doc(db, 'templates', templateToDelete));
      setTemplates(prev => prev.filter(t => t.id !== templateToDelete));
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Failed to delete template');
    } finally {
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  const handleEditTemplate = async (templateId) => {
    try {
      const templateRef = doc(db, 'templates', templateId);
      const templateSnap = await getDoc(templateRef);
      
      if (!templateSnap.exists()) {
        throw new Error('Template not found');
      }

      const templateData = templateSnap.data();
      
      if (templateData.userId !== currentUser?.uid) {
        throw new Error('Permission denied');
      }

      navigate(`/forms/templates/${templateId}/edit`, {
        state: { templateData }
      });
    } catch (error) {
      console.error('Error accessing template:', error);
      setError(
        error.message === 'Permission denied'
          ? 'You do not have permission to edit this template'
          : 'Failed to load template for editing'
      );
    }
  };

  useEffect(() => {
    const handleTemplateCreated = (event) => {
      const newTemplate = event.detail;
      setTemplates(prev => [...prev, newTemplate]);
    };

    window.addEventListener('templateCreated', handleTemplateCreated);
    return () => window.removeEventListener('templateCreated', handleTemplateCreated);
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const templatesRef = collection(db, 'templates');
    const q = query(
      templatesRef,
      where('userId', '==', currentUser.uid),
      where('isTemplate', '==', true)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const changes = snapshot.docChanges();
      
      setTemplates(prev => {
        let updated = [...prev];
        
        changes.forEach(change => {
          const template = { id: change.doc.id, ...change.doc.data() };
          
          if (change.type === 'added') {
            if (!updated.some(t => t.id === template.id)) {
              updated.push(template);
            }
          } else if (change.type === 'modified') {
            updated = updated.map(t => 
              t.id === template.id ? template : t
            );
          } else if (change.type === 'removed') {
            updated = updated.filter(t => t.id !== template.id);
          }
        });
        
        return updated;
      });
    }, (error) => {
      console.error('Error in template listener:', error);
      setError('Failed to sync templates. Please refresh.');
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="my-lists-container">
      <header className="header">
        <nav className="navbar">
          <div className="logo">
            <Link to="/">MOMOH</Link>
          </div>
          
          <div className="nav-controls">
            {currentUser && (
              <button 
                className="menu-button"
                onClick={handleMenuClick}
                aria-label="Toggle menu"
              >
                {isOpen ? <FaTimes /> : <FaBars />}
              </button>
            )}
          </div>
        </nav>
      </header>

      {isOpen && currentUser && (
        <div 
          className="menu-overlay"
          onClick={(e) => e.target.classList.contains('menu-overlay') && setIsOpen(false)}
        >
          <div className="menu-content">
            <Link 
              to="/" 
              className={location.pathname === '/' ? 'active' : ''}
              onClick={() => handleLinkClick('/')}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={location.pathname === '/about' ? 'active' : ''}
              onClick={() => handleLinkClick('/about')}
            >
              About
            </Link>
            <Link 
              to="/dashboard" 
              className={location.pathname === '/dashboard' ? 'active' : ''}
              onClick={() => handleLinkClick('/dashboard')}
            >
              Dashboard
            </Link>
            <Link 
              to="/foodback" 
              className={location.pathname === '/foodback' ? 'active' : ''}
              onClick={() => handleLinkClick('/foodback')}
            >
              Foodback
            </Link>
            <Link 
              to="/forms" 
              className={location.pathname.startsWith('/forms') ? 'active' : ''}
              onClick={() => handleLinkClick('/forms')}
            >
              Forms
            </Link>
            <Link 
              to="/manage" 
              className={location.pathname.startsWith('/manage') ? 'active' : ''}
              onClick={() => handleLinkClick('/manage')}
            >
              Manage
            </Link>
            <Link 
              to="/active-forms" 
              className={location.pathname === '/active-forms' ? 'active' : ''}
              onClick={() => handleLinkClick('/active-forms')}
            >
              <FaClipboardCheck className="menu-icon" />
              Active Forms
            </Link>
            <Link 
              to="/waste" 
              className={location.pathname === '/waste' ? 'active' : ''}
              onClick={() => handleLinkClick('/waste')}
            >
              Waste
            </Link>
            <div className="menu-footer">
              <span className="welcome-text">Welcome, {currentUser.email}</span>
              <button onClick={handleLogout} className="logout-button">
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="content-wrapper">
        <div className="page-header">
          {hasPermission('CREATE_TEMPLATE') && (
            <Link to="/forms" className="create-template-btn">
              Create New Template
            </Link>
          )}
        </div>
        
        <div className="templates-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Type</th>
                <th>Locations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(template => (
                <tr key={template.id}>
                  <td>{template.name}</td>
                  <td>{template.description}</td>
                  <td>{template.type}</td>
                  <td>
                    <div className="template-locations">
                      {template.locations?.map(location => (
                        <span key={location} className="location-tag">
                          {location}
                        </span>
                      ))}
                      {(!template.locations || template.locations.length === 0) && (
                        <span className="location-tag">No locations assigned</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="template-actions">
                      <FaPlay 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTemplateClick(template.id);
                        }}
                        className="action-icon use-icon"
                        title="Use Template"
                      />
                      <FaEdit 
                        onClick={() => navigate(`/forms/tasklists/edit/${template.id}`)}
                        className="action-icon edit-icon"
                        title="Edit Template"
                      />
                      <FaTrash 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTemplateDelete(template.id);
                        }}
                        className="action-icon delete-icon"
                        title="Delete Template"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete this template?</p>
            <div className="modal-actions">
              <button 
                onClick={confirmDelete}
                className="confirm-delete-btn"
              >
                Yes, Delete
              </button>
              <button 
                onClick={() => {
                  setShowDeleteModal(false);
                  setTemplateToDelete(null);
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyLists;